<template>
  <Card>
    <h1 class="text-h2 text-center mb-16">Let's find the sitters closest to you</h1>
    <p class="text-body text-center mb-24">We have sitters in 96% of U.S. neighborhoods.</p>
    <form>
      <div class="mb-32">
        <TextField
          id="zipCode"
          v-model="zipCode"
          labelName="ZIP code"
          name="postal-code"
          type="text"
          maxlength="5"
          pattern="[0-9]*"
          inputmode="numeric"
          :aria-label="errors.zipCode || 'Five-digit zip code'"
          :status="helperStatus"
          :helperText="errors.zipCode || 'Please enter a five-digit ZIP code.'"
        />
      </div>
      <div class="flex flex-row-reverse">
        <RouteStepperNextButton :draftData="draftData" :validate="validateFunc" @errors="errors = $event" />
      </div>
    </form>
  </Card>
</template>

<script lang="ts">
import { HelperStatus } from '@/helpers/status';
import { isValidZipcode } from '@/helpers/addressHelper';
import { geocode, type GeocodeResult } from '@/lib/geocode';
import TextField from '@/components/textfield/TextField.vue';
import Card from '@/components/card/Card.vue';
import RouteStepperNextButton from '@/components/route_stepper/RouteStepperNextButton.vue';
import { OnboardingSeekerDraft } from '../types';
import type { PropType } from 'vue';

interface Errors {
  zipCode?: string;
}

interface OnboardingSeekerLocationData extends OnboardingSeekerDraft {
  errors: Errors;
}

export default {
  name: 'OnboardingSeekerLocation',
  components: {
    Card,
    TextField,
    RouteStepperNextButton,
  },
  props: {
    draft: {
      type: Object as PropType<OnboardingSeekerDraft>,
      required: true,
    },
  },
  data(): OnboardingSeekerLocationData {
    return {
      zipCode: this.draft?.zipCode ?? null,
      location: null,
      errors: {},
    };
  },
  computed: {
    draftData(): OnboardingSeekerDraft {
      return {
        zipCode: this.zipCode,
        location: this.location,
      };
    },
    helperStatus(): HelperStatus {
      if (this.errors.zipCode) {
        return HelperStatus.Error;
      }
      return HelperStatus.Neutral;
    },
  },
  watch: {
    zipCode(): void {
      this.resetValidation();
      this.location = null;
    },
  },
  methods: {
    resetValidation(): void {
      this.errors = {};
    },
    async geocodeLocation(): Promise<GeocodeResult | null> {
      try {
        return await geocode(this.zipCode);
      } catch (error) {
        console.error('Error verifying location:', error);
        return null;
      }
    },
    async validateFunc(): Promise<Errors> {
      const errors: Errors = {};
      if (!isValidZipcode(this.zipCode)) {
        errors.zipCode = 'ZIP code is required. Enter a five digit ZIP code.';
        return errors;
      }
      this.location = (await this.geocodeLocation())?.location ?? null;

      if (!this.location) {
        errors.zipCode = 'Unable to find location. Please check the ZIP code.';
      }
      return errors;
    },
  },
};
</script>
